<template>
  <div
    class="custom-select-container"
  >
    <div
      ref="custom-font-select-element"
      :class="[`custom-select ${customSizing} custom-select-element`, {'is-focused': isFocused, 'disabled': disabled}]"
      tabindex="0"
      @click="handleClickSelect($event)"
      data-role="custom-font-select"
    >
      <p
        ref="selected-option"
        :class="['mb-0 w-100 ellipsis']"
        data-role="custom-select-selected"
        data-test="selected-option"
      >
        {{ fontSelected }}
      </p>
      <div
        v-show="isOpen"
        :class="['list-options-container card border-1 rounded']"
      >
        <div
          :class="['font-search-input-container p-2']"
        >
          <input
            id="font-search-input"
            ref="font-search-input"
            type="text"
            placeholder="Search"
            :class="['form-control font-search-input']"
            :value="fontSearchQuery"
            @input="searchFonts($event)"
            @click="handleClickSelect($event)"
            autocomplete="off"
          >
        </div>
        <ul
          ref="list-options"
          :class="['list-options mb-0']"
          @mouseenter="setIsMouseOverList(true)"
          @mouseleave="setIsMouseOverList(false)"
          data-role="custom-select-options-list"
        >
          <li
            v-for="(option, index) in filteredOptionsList"
            :key="index"
            :ref="`list-option-${hoveredIndex === index ? 'highlighted' : index}`"
            :class="['list-option d-flex justify-content-between align-items-center', {'bg-light': isHighlighted(option, index)}]"
            @mouseover="setHoveredIndex(index)"
            @click="handleClickOption(option)"
            data-role="custom-select-option"
          >
            <p
              class="mb-0 flex-grow-1"
              :style="getFontFamilyStyle(option.fontFamily)"
            >
              {{ option.fontFamily }}
            </p>
            <i
              v-if="option.fontFamily === value"
              class="fal fa-check"
            />
          </li>
        </ul>
      </div>
    </div>
    <div
      :class="{'is-paused': disabled}"
    />
  </div>
</template>
<script>
import googleFonts from '@/fonts';

import objectHelper from '@/core/mixins/objectHelper';

/**
 * ? *) Focus the input on open.
 */

function initialData() {
  return {
    fontSelected: null,
    isOpen: false,
    isFocused: false,
    isMouseOverList: false,
    hoveredIndex: null,
    fontSearchQuery: '',
    scrolledAmount: 0,
  };
}
export default {
  name: 'BaseFontSelect',
  props: {
    size: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: 'Roboto',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [
    objectHelper,
  ],
  data() {
    return initialData();
  },
  computed: {
    customSizing() {
      return this.size ? `custom-select-${this.size}` : '';
    },
    optionsList() {
      const categories = ['display', 'sans-serif', 'serif', 'monospace'];
      const subset = 'latin-ext';
      const filteredFonts = [];
      for (let index = 0; index < googleFonts.length; index++) {
        const font = googleFonts[index];
        if (categories.includes(font.category)
          && font.subsets.includes(subset)) {
          filteredFonts.push({
            fontFamily: font.family,
            category: font.category,
            index: font.alphaIndex,
          });
        }
      }
      return filteredFonts;
    },
    filteredOptionsList() {
      if (!this.fontSearchQuery) return this.optionsList;
      return this.optionsList
        .filter((font) => new RegExp(`^${this.fontSearchQuery.trim()}`, 'i').test(font.fontFamily));
    },
    selectedValueIndex() {
      return this.filteredOptionsList.findIndex((font) => font.fontFamily === this.value);
    },
    listItemOffsetHeight() {
      return this.$refs['list-option'][0]?.offsetHeight || 0;
    },
  },
  methods: {
    getFontFamilyStyle(fontFamily) {
      return {
        'font-family': fontFamily,
      };
    },
    searchFonts(event) {
      this.fontSearchQuery = event.target.value;
      this.setHoveredIndex();
      this.handleScroll({ scrollToSelected: true });
    },
    isHighlighted(option, index) {
      return option.fontFamily === this.value
         || (typeof this.hoveredIndex === 'number' && this.hoveredIndex === index);
    },
    setIsMouseOverList(isOver) {
      this.isMouseOverList = isOver;
    },
    setHoveredIndex(index = null) {
      if (typeof index !== 'number') {
        this.hoveredIndex = this.selectedValueIndex !== -1 ? this.selectedValueIndex : 0;
      } else this.hoveredIndex = index;
    },
    async handleScroll({ scrollToSelected = false, direction = null } = {}) {
      if (!scrollToSelected && !direction) return;
      await this.$nextTick();
      const optionsList = this.$refs['list-options'];
      const containerHeight = optionsList.offsetHeight;
      const highlightedOption = this.$refs['list-option-highlighted'][0];
      if (scrollToSelected && !direction) {
        optionsList.scroll({
          top: highlightedOption?.offsetTop || 0,
        });
        return;
      }
      const viewPortPosition = optionsList?.scrollTop + optionsList?.offsetHeight;
      const isAboveView = highlightedOption?.offsetTop < optionsList?.scrollTop;
      const isBelowView = highlightedOption?.offsetTop + highlightedOption?.offsetHeight > viewPortPosition;
      if (!isAboveView && !isBelowView) return;
      let toScroll = 0;
      if (direction === 'down') toScroll = (highlightedOption?.offsetTop - containerHeight) + highlightedOption?.offsetHeight;
      else toScroll = highlightedOption?.offsetTop;
      optionsList.scroll({
        top: toScroll,
        left: 0,
        behavior: 'auto',
      });
    },
    handleClickSelect(event) {
      if (event.target === this.$refs['font-search-input']) return;
      event.stopImmediatePropagation();
      this.isOpen = !this.isOpen;
      if (this.isOpen) this.handleFocus();
    },
    handleClickOption(option) {
      this.fontSelected = option.fontFamily;
      this.$emit('updateValue', option.fontFamily);
    },
    handleKeyDown(event) {
      const optionsListKeys = ['Enter', 'ArrowUp', 'ArrowDown', 'Escape'];
      if (event.target === this.$refs['font-search-input']
        && !optionsListKeys.includes(event.key)
      ) {
        this.searchFonts(event);
        return;
      }
      event.preventDefault();
      if (event.key === 'ArrowUp') {
        if (!this.isOpen) this.isOpen = true;
        else if (this.hoveredIndex !== 0) {
          this.setHoveredIndex(this.hoveredIndex - 1);
          this.handleScroll({ direction: 'up' });
        }
        return;
      }
      if (event.key === 'ArrowDown') {
        if (!this.isOpen) this.isOpen = true;
        else if (this.hoveredIndex < this.optionsList.length - 1) {
          this.setHoveredIndex(this.hoveredIndex + 1);
          this.handleScroll({ direction: 'down' });
        }
        return;
      }
      if (event.key === 'Enter') {
        if (!this.isOpen) this.isOpen = true;
        else {
          this.handleClickOption(this.optionsList[this.hoveredIndex]);
          // Submit font to BE (Keep list open to allow user to test fonts)
          // this.$emit();
        }
        return;
      }
      if (event.key === 'Escape') this.handleBlur();
    },
    async handleFocus() {
      if (this.isFocused) return;
      this.isFocused = true;
      this.$emit('focus');
      document.addEventListener('click', this.handleDocumentClick, { once: true });
      await this.$nextTick();
      this.$refs['font-search-input'].focus();
      this.setHoveredIndex();
      this.handleScroll({ scrollToSelected: true });
    },
    handleDocumentClick(event) {
      if (!this.isFocused || event.target === this.$refs['font-search-input']) {
        document.addEventListener('click', this.handleDocumentClick, { once: true });
        return;
      }
      if (!event.target?.dataset?.role?.includes('custom-select')) {
        this.isOpen = false;
        this.isFocused = false;
        this.handleBlur();
      }
    },
    handleBlur() {
      this.$emit('blur');
      if (this.$refs['custom-font-select-element']) this.$refs['custom-font-select-element'].blur();
    },
  },
  created() {
    this.fontSelected = this.value;
  },
  mounted() {
    const selectElement = this.$refs['custom-font-select-element'];
    selectElement.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    const selectElement = this.$refs['custom-font-select-element'];
    selectElement.removeEventListener('keydown', this.handleKeyDown);
  },
};
</script>
<style scoped lang="scss">
.custom-select-container {
  position: relative;
}
.custom-select-element {
  position: relative;
  display: flex !important;
  align-items: center;
  &.is-focused {
    border-color: $link;
    outline: .2rem solid rgba(0, 123, 255, 0.25);
  }
  &.disabled {
    color: #6c757d;
    background-color: #e9ecef;
  }
}
.list-options-container {
  position: absolute;
  width: 100%;
  height: 216px;
  overflow: hidden;
  top: 108%;
  left: 0;
  z-index: 1;
  box-shadow: 0 3px 8px rgba(0,0,0,0.35);
}
.list-options {
  position: relative;
  list-style: none;
  overflow-y: auto;
  padding: 0;
  .list-option {
    padding: .25rem .75rem;
  }
}
.is-paused {
  position: absolute;
  cursor: not-allowed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
